/**
 * useInboxUnread — polls /api/interactions/unread-count every 20s, spawns a
 * browser Notification when the count increases, and updates document.title.
 *
 * Deliberate design choices:
 *   • Polling (not SSE / WebSocket) because k8s ingress can idle long-lived
 *     connections and this hook has to survive every routing scenario.
 *   • Notification.requestPermission() only fires from a direct user gesture,
 *     so the hook exposes `requestPermission()` that the CRM Inbox page wires
 *     to a real button — never auto-prompted on page load (that gets denied).
 *   • When count goes UP, we fetch just the new items via `?since=<iso>` so
 *     the notification title can name the caller (e.g. "New call from Sarah").
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../context/AuthContext';

const POLL_MS = 20_000;
const TITLE_BASE = 'LISTING AiGENT';

let originalTitle = typeof document !== 'undefined' ? document.title : TITLE_BASE;

function updateTitle(count) {
  if (typeof document === 'undefined') return;
  if (count > 0) document.title = `(${count}) ${originalTitle || TITLE_BASE}`;
  else document.title = originalTitle || TITLE_BASE;
}

function notificationSupported() {
  return typeof window !== 'undefined' && 'Notification' in window;
}

function notify(item) {
  if (!notificationSupported() || Notification.permission !== 'granted') return;
  const who = item.contact || item.number || 'Unknown';
  const title = item.type === 'call' ? `New call from ${who}` : `New SMS from ${who}`;
  const body = item.transcript
    || (item.messages && item.messages.slice(-1)[0]?.text)
    || 'Open your inbox to see the message.';
  try {
    const n = new Notification(title, { body, icon: '/favicon.ico', tag: `inbox-${item.id}`, renotify: false });
    n.onclick = () => { window.focus(); window.location.href = '/crm/inbox'; n.close(); };
  } catch {
    // Some browsers throw if permission is granted but the page is backgrounded;
    // silently drop — the unread badge still updates.
  }
}

export default function useInboxUnread({ user }) {
  const [count, setCount] = useState(0);
  const [permission, setPermission] = useState(
    notificationSupported() ? Notification.permission : 'denied',
  );
  const lastCountRef = useRef(0);
  const lastSeenAtRef = useRef(new Date().toISOString());

  const requestPermission = useCallback(async () => {
    if (!notificationSupported()) return 'denied';
    try {
      const p = await Notification.requestPermission();
      setPermission(p);
      return p;
    } catch {
      return 'denied';
    }
  }, []);

  const poll = useCallback(async () => {
    try {
      const { data } = await api.get('/interactions/unread-count');
      const next = Number(data?.count || 0);
      const prev = lastCountRef.current;
      lastCountRef.current = next;
      setCount(next);
      updateTitle(next);
      if (next > prev) {
        // Fetch just the items that arrived since we last checked
        const { data: fresh } = await api.get('/interactions', {
          params: { since: lastSeenAtRef.current, limit: 5 },
        });
        // Response is sorted at desc — walk oldest→newest so notifications appear in order
        for (const item of (fresh || []).slice().reverse()) {
          if (item.at) lastSeenAtRef.current = item.at;
          notify(item);
        }
      }
    } catch {
      // Silent — bad networks are common; next poll will retry.
    }
  }, []);

  useEffect(() => {
    if (!user) { updateTitle(0); return undefined; }
    // First poll delayed 500ms so we don't fight the initial /auth/me fetch
    const first = setTimeout(poll, 500);
    const interval = setInterval(poll, POLL_MS);
    return () => { clearTimeout(first); clearInterval(interval); };
  }, [user, poll]);

  return { count, permission, requestPermission, poll };
}
